import { FiDialogsService } from '@fi-shared/fi-dialogs';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CheckpointModel } from '../checkpoint.model';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgForm,
    Validator,
} from '@angular/forms';
import { FiChecklistCheckpointAnswerComponent } from '../checkpoint-answer/checkpoint-answer.component';
import { CheckpointStatusType } from '@fi-shared/checklist';
import { CommentModel } from '@fi-shared/models/comment.model';
import {
    IFiChecklistAnswerModel,
    IFiChecklistModelBase,
    IFiChecklistViewerModel,
} from '@fi-shared/checklist/checklist';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiChecklistCheckpointViewAdditionalInfoComponent } from '@fi-app/checklist/checkpoint-view-additional-info/checkpoint-view-additional-info.component';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { IMediaBankFile } from '@fi-shared/media-bank';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-checklist-checkpoint-view',
    templateUrl: './checkpoint-view.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./checkpoint-view.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: FiChecklistCheckpointViewComponent, multi: true },
        { provide: NG_VALIDATORS, useExisting: FiChecklistCheckpointViewComponent, multi: true },
        { provide: NgForm, useExisting: FiChecklistCheckpointViewComponent },
    ],
    standalone: false,
})
export class FiChecklistCheckpointViewComponent implements OnInit, ControlValueAccessor, Validator {
    @Input('ngModel') checkpoint: CheckpointModel;
    @Input() checklist: IFiChecklistModelBase;
    @Input() checklistViewer: IFiChecklistViewerModel;
    @Input() checkpointAnswer: IFiChecklistAnswerModel;
    @Input() hasValidQRCode: boolean = false;
    @Input() answerDisabled: boolean;
    @Input() submitted: boolean;
    @Input() addFileDisabled: boolean;
    @Input() showMenu: boolean = false;
    @Input() menuDisabled: boolean;
    @Input() showBusinessUnit: boolean = false;
    @Input() selectedLanguageId: number;

    @Output() onAddMedia = new EventEmitter();
    @Output() onMediaRemove = new EventEmitter();
    @Output() onAnswerValidationChange = new EventEmitter();
    @Output() onAnswerChange = new EventEmitter();
    @Output() onSetDirty = new EventEmitter();
    @Output() onNotApplicationToggle = new EventEmitter();
    @Output() onAnswerCleared = new EventEmitter();
    @Output() onCommentChange = new EventEmitter();

    @ViewChild('checkpointForm', { static: true }) checkpointForm: NgForm;
    @ViewChild(FiChecklistCheckpointAnswerComponent, { static: true })
    checklistCheckpointAnswerComponent: FiChecklistCheckpointAnswerComponent;
    @ViewChild(FiChecklistCheckpointViewAdditionalInfoComponent, { static: true })
    checklistCheckpointAdditionalInfoComponent: FiChecklistCheckpointViewAdditionalInfoComponent;

    answerValid: boolean = true;

    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private fileUploadService: FiFileUpload2Service,
        private mediaBankHelperService: MediaBankHelperService,
        private cdr: ChangeDetectorRef,
        private userDataService: FiUserDataService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    isCheckpointNotApplicable() {
        return (
            (this.checkpoint.CheckPointSetting && this.checkpoint.CheckPointSetting.IsSetNotApplicable) ||
            this.checklist.IsSetNotApplicable
        );
    }

    ngOnInit() {
        if (this.checkpointAnswer.Comments.length === 0) {
            const newComment = new CommentModel(this.fiFormatDateService);
            newComment.ContentLanguageId = this.userDataService.userData.PreferredContentLanguage;
            this.checkpointAnswer.Comments.push(newComment);
        }
    }

    getPreferredContentLabelValue(items: IContentLanguageLabelModel[]) {
        if (items && items.length) {
            const preferredContentLabel = items.find((item) => item.ContentLanguageId === this.selectedLanguageId);
            return preferredContentLabel ? preferredContentLabel.Value : '';
        }
        return '';
    }

    addPhotoDoneClick(mediaFile: IMediaBankFile) {
        const mediaReference = this.mediaBankHelperService.getMediaBankFileReferenceFromMediaFile(mediaFile, {
            isNewUpload: true,
            showInMediaBank: false,
        });
        this.checkpointAnswer.MediaBankFileReferences[0] = mediaReference;
        this.setDirty();
        this.onAddMedia.emit();
    }

    initialRemoved = false;

    // note: not used
    // removePhotoProcess() {
    //     this.fiDialogsService.confirm({
    //       headerText: this.appTextService.get('dialog.remove_attachment_header'),
    //       messageText: this.appTextService.get('dialog.delete_confirm')
    //     }).onYesButtonClick(() => {
    //         const answer = this.checkpointAnswer;
    //         if (this.initialRemoved === false &&
    //             answer.$$mediaFileReferenceInitial &&
    //             answer.$$mediaFileReferenceInitial !== answer.MediaBankFileReferences[0]) {
    //             this.checkpointAnswer.MediaBankFileReferences = [
    //                 answer.$$mediaFileReferenceInitial
    //             ];
    //         } else {
    //             this.checkpointAnswer.MediaBankFileReferences.length = 0;
    //             this.initialRemoved = true;
    //         }
    //         this.setDirty();
    //         this.onMediaRemove.emit();
    //     });
    // }
    //
    // onRemovePhoto() {
    //     this.removePhotoProcess();
    // }

    onRemoveComment() {
        this.checkpointAnswer.Comments[0].Content = '';
        this.checkpointAnswer.Comments = this.checkpointAnswer.Comments.slice();
        this.setDirty();
    }

    // note: not used
    // removeFileMediaReference() {
    //     this.removePhotoProcess();
    // }

    selectPhotoMobile(files) {
        this.loadingService.trackPromise(
            this.fileUploadService
                .uploadFiles({
                    files: files,
                })
                .then((result) => this.addPhotoDoneClick(result[0])),
        );
    }

    getAttachFileText() {
        return this.checkpointAnswer.MediaBankFileReferences.length > 0
            ? 'checklist.replace_photo_video'
            : 'checklist.add_photo_video';
    }

    isAttachmentRequired() {
        return (
            this.checkpointAnswer.Answer &&
            ((this.checkpoint.IsAttachmentMandatoryOk && this.checkpointAnswer.Answer === CheckpointStatusType.Ok) ||
                (this.checkpoint.IsAttachmentMandatoryNotOk &&
                    this.checkpointAnswer.Answer === CheckpointStatusType.NotOk))
        );
    }

    isCommentRequired() {
        return (
            this.checkpointAnswer.Answer &&
            ((this.checkpoint.IsCommentMandatoryOk && this.checkpointAnswer.Answer === CheckpointStatusType.Ok) ||
                (this.checkpoint.IsCommentMandatoryNotOk &&
                    this.checkpointAnswer.Answer === CheckpointStatusType.NotOk))
        );
    }

    answerOnChange() {
        this.setDirty();
        this.onAnswerChange.emit();
    }

    answerValidationChange(checkpoint, $event) {
        this.answerValid = $event;
        this._onValidatorChange();
    }

    isShowAttachmentError() {
        return (
            this.isAttachmentRequired() &&
            !(this.checkpointAnswer.MediaBankFileReferences && this.checkpointAnswer.MediaBankFileReferences.length > 0)
        );
    }

    isShowCommentError() {
        return (
            this.isCommentRequired() &&
            !(this.checkpointAnswer.Comments && this.checkpointAnswer.Comments.find((c) => !!c.Content))
        );
    }

    setDirty() {
        this.checkpointForm.control.markAsDirty();
        this.onSetDirty.emit();
    }

    firstWriteValue = true;
    writeValue(v: CheckpointModel): void {
        if (!this.firstWriteValue && v !== null) {
            this.checkpoint = v;
            this.firstWriteValue = false;
        }
    }

    // note: not used ?
    registerOnChange(fn: any) {}

    // note: not used ?
    registerOnTouched(fn: any) {}

    validate(c: AbstractControl) {
        if (this.checklistCheckpointAnswerComponent.isValid()) {
            return null;
        }

        return {
            answer: true,
        };
    }

    _onValidatorChange: () => void = () => {};

    registerOnValidatorChange(fn: () => void) {
        this._onValidatorChange = fn;
    }

    onCheckpointAnswerCommentChange() {
        this.setDirty();
        this.onCommentChange.emit();
    }

    onCheckpointAnswerAttahmentsChanged() {
        this.setDirty();
    }

    isQrCodeRequired() {
        return this.checklist.CheckPresenceEnabled && this.checkpoint.CheckPresence;
    }

    onChecklistAnswersChanged() {
        this.checklistCheckpointAnswerComponent.onChecklistAnswersChanged();
        this.checklistCheckpointAdditionalInfoComponent.updateEditData();
        this.cdr.detectChanges();
    }
}
